import { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Check, Loader2, Play, RefreshCw, Undo2, X } from 'lucide-react';
import { integrationApi, type IntegrationProposal } from '@/services/api';

const KIND_LABEL: Record<IntegrationProposal['kind'], string> = {
  same_as: 'Same as',
  related_to: 'Related to',
  gap: 'Gap in client graph',
  off_category: 'Not this category',
};
const KIND_STYLE: Record<IntegrationProposal['kind'], string> = {
  same_as: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  related_to: 'bg-sky-50 text-sky-700 border-sky-200',
  gap: 'bg-amber-50 text-amber-700 border-amber-200',
  off_category: 'bg-slate-100 text-slate-600 border-slate-300',
};

/**
 * Links the knowledge-integration agent proposes from this library into a member
 * client's graph. Nothing reaches the core graph until a person approves it.
 */
export function DocketProposalsTab({
  docketId,
  clientUid,
  onOpenItem,
}: {
  docketId: string;
  clientUid: string | null;
  onOpenItem?: (itemId: string) => void;
}) {
  const [rows, setRows] = useState<IntegrationProposal[] | null>(null);
  const [kind, setKind] = useState<IntegrationProposal['kind'] | 'all'>('all');
  const [status, setStatus] = useState<IntegrationProposal['status'] | 'all'>('pending');
  const [run, setRun] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!clientUid) return;
    integrationApi
      .list(docketId, clientUid)
      .then((r) => setRows(r.proposals))
      .catch((e: Error) => setError(e.message));
  }, [docketId, clientUid]);
  useEffect(load, [load]);

  // While a run is going, poll it and refresh as proposals land.
  useEffect(() => {
    if (run !== 'running' || !clientUid) return;
    const t = setInterval(() => {
      integrationApi.status(docketId, clientUid).then((s) => {
        setRun(s.status);
        if (s.status !== 'running') load();
        if (s.error) setError(s.error);
      });
    }, 5000);
    return () => clearInterval(t);
  }, [run, docketId, clientUid, load]);

  const decide = (p: IntegrationProposal, next: IntegrationProposal['status']) => {
    setBusy(p.id);
    integrationApi
      .decide(docketId, p.id, next)
      .then((updated) => setRows((rs) => rs?.map((r) => (r.id === p.id ? { ...r, ...updated } : r)) ?? null))
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(null));
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows ?? []) {
      c[r.kind] = (c[r.kind] ?? 0) + 1;
      c[r.status] = (c[r.status] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  const shown = (rows ?? []).filter((r) => (kind === 'all' || r.kind === kind) && (status === 'all' || r.status === status));

  if (!clientUid) return <p className="text-sm text-slate-500">Choose a member client above.</p>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-3xl">
          The integration agent reads this library in the Knowledge Explorer and {clientUid}&apos;s own graph, and proposes
          how each buyer concept joins it: the same concept, a related one, or a gap the client&apos;s graph does not
          hold. Approving records the decision; nothing is written to the core graph until a promote applies the
          approved links.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy-900"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            disabled={run === 'running'}
            onClick={() =>
              integrationApi
                .start(docketId, clientUid)
                .then((s) => setRun(s.status))
                .catch((e: Error) => setError(e.message))
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-navy-900 text-white px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {run === 'running' ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {run === 'running' ? 'Agent running…' : 'Run integration agent'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex justify-between">
          {error}
          <button type="button" onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex gap-1">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={clsx('rounded-full border px-2 py-0.5', status === s ? 'border-navy-900 text-navy-900' : 'border-slate-200 text-slate-500')}
            >
              {s} {s !== 'all' && counts[s] ? counts[s] : ''}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(['all', 'same_as', 'related_to', 'gap', 'off_category'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={clsx('rounded-full border px-2 py-0.5', kind === k ? 'border-navy-900 text-navy-900' : 'border-slate-200 text-slate-500')}
            >
              {k === 'all' ? 'all kinds' : KIND_LABEL[k]} {k !== 'all' && counts[k] ? counts[k] : ''}
            </button>
          ))}
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading proposals…
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-slate-500">
          {rows.length ? 'Nothing matches these filters.' : 'No proposals yet. Run the integration agent to create them.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {shown.map((p) => (
            <li key={p.id} className="py-3 grid grid-cols-[1fr_auto] gap-3">
              <div className="space-y-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <button
                    type="button"
                    onClick={() => onOpenItem?.(p.library_item.id)}
                    className="font-medium text-navy-900 hover:text-brand-teal"
                  >
                    {p.library_item.name}
                  </button>
                  <span className={clsx('text-[11px] rounded-full border px-2 py-0.5', KIND_STYLE[p.kind])}>
                    {KIND_LABEL[p.kind]}
                  </span>
                  {p.target && (
                    <>
                      <span className="text-navy-900">{p.target.name}</span>
                      <span className="text-[11px] text-slate-400">
                        {p.target.entity_class.replace(/_/g, ' ')} · {p.target.resolved_id}
                      </span>
                    </>
                  )}
                  <span className="text-[11px] text-slate-400">
                    confidence {Math.round(p.confidence * 100)}% · {p.model}
                  </span>
                </div>
                <p className="text-xs text-slate-600">{p.reasoning}</p>
                {p.products && p.products.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Nearest products:{' '}
                    {p.products
                      .slice(0, 3)
                      .map((x) => `${x.name}${x.type ? ` (${x.type})` : ''}`)
                      .join(' · ')}
                  </p>
                )}
                {p.second_opinion && !p.second_opinion.agrees && (
                  <p className="text-xs rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                    Look closer: {p.second_opinion.model} says{' '}
                    <span className="font-medium">
                      {KIND_LABEL[p.second_opinion.kind as IntegrationProposal['kind']] ?? p.second_opinion.kind}
                      {p.second_opinion.target ? ` ${p.second_opinion.target}` : ''}
                    </span>
                    . {p.second_opinion.reasoning}
                  </p>
                )}
                {!!p.evidence?.explorer_links?.length && (
                  <p className="text-[11px] text-slate-400 truncate">
                    Explorer: {p.evidence.explorer_links.slice(0, 6).join(' · ')}
                  </p>
                )}
                {p.status !== 'pending' && (
                  <p className="text-[11px] text-slate-500">
                    {p.status} by {p.decided_by} {p.decided_at?.slice(0, 16).replace('T', ' ')}
                  </p>
                )}
              </div>
              <div className="flex items-start gap-1">
                {busy === p.id ? (
                  <Loader2 size={14} className="animate-spin text-slate-400" />
                ) : p.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => decide(p, 'approved')}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50"
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(p, 'rejected')}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      <X size={12} /> Reject
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => decide(p, 'pending')}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy-900"
                  >
                    <Undo2 size={12} /> Undo
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
